/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useMemo, useRef } from 'react';
import { Asset, useLoadAsset, MimeType } from '@facephi/inphinite-common';
import { Icon, Label, StatusType } from '@facephi/ui-react';
import { useTranslation } from 'react-i18next';
import Skeleton from 'react-loading-skeleton';
import { infoVoice, tInfo } from './model';
import { OperationSource } from './operationsCards';
import { OperationsCardIcon } from './operationsCards/Styles';
import {
  FacialCheckGrid,
  VoiceContainer,
  FacialResultIconWrapper,
  InfoContainer,
} from './Styles';
import { useOperations } from '../../providers';
import { ZOOM } from '../../state/constants';
import { OperationStatus } from '../../state/model';

type Props = {
  status?: StatusType;
  assets?: Asset[];
  loading?: boolean;
  family?: string;
};

const ImageSkeleton = () => (
  <div>
    <Skeleton width={120} height={120} borderRadius={8} />
    <div style={{ marginTop: '0.8rem' }}>
      <Skeleton width="50%" height={18} borderRadius={8} />
    </div>
  </div>
);

export const VoiceValidation = React.forwardRef(
  ({ status, assets = [], loading }: Props) => {
    const { t } = useTranslation();
    const { token } = useOperations();
    const imageRef = useRef<HTMLImageElement>(null);

    const audioAsset = useMemo(
      () =>
        assets.length
          ? assets.find(({ contentType }) => contentType.includes('audio'))
          : undefined,
      [assets],
    );

    const { resourceUrl } = useLoadAsset(
      audioAsset?.url,
      audioAsset?.contentType,
      token,
    );

    const getLoadingAudios = () => (
      <>
        <ImageSkeleton />
        <Skeleton width={40} height={40} circle />
      </>
    );

    const info: tInfo = useMemo(() => {
      if (!status) return infoVoice['notAble'];
      const type = status === OperationStatus.succeeded ? 'success' : 'error';
      return infoVoice[type];
    }, [status]);

    const getAudios = () => (
      <>
        <VoiceContainer flexDirection="column" rowGap="0.8">
          <OperationSource
            ref={imageRef}
            resource={resourceUrl}
            title="voice"
            zoom={ZOOM.defaultZoom}
            mimeType={audioAsset?.contentType as MimeType}
            hasAssetError={false}
            flexDirectionError="column"
            zoomable
            controls
            autoPlay
          />
        </VoiceContainer>

        {info && (
          <OperationsCardIcon
            $bgColor={info.backgroundColor}
            $remSize="6rem"
            key={`info`}
            id="voice-match-icon"
            $borderRadius="1.7rem"
            className="facial-match-animated"
          >
            <Icon
              iconName={info.iconName}
              size={info.size}
              color={info?.iconColor}
              weight={info.weight}
            />
          </OperationsCardIcon>
        )}
      </>
    );

    return (
      <>
        <FacialCheckGrid
          alignItems="center"
          columnGap="0.8"
          justifyContent="space-between"
          gridTemplateColumns="1fr 6rem"
          gridTemplateRows="10rem auto"
          rowGap="1.2"
        >
          {loading ? getLoadingAudios() : getAudios()}
          {!loading && (
            <div>
              <Label className="asset-text" fontSize="12">
                {t('Voice Authentication')}
              </Label>
            </div>
          )}
        </FacialCheckGrid>
        {loading ? (
          <div style={{ marginTop: '2rem', textAlign: 'center' }}>
            <Skeleton width="80%" height={18} borderRadius={8} />
          </div>
        ) : (
          <InfoContainer
            $infoBackground={info?.backgroundColor}
            $infoColor={info?.iconColor}
            rowGap="0.8"
            flexDirection="row"
            alignItems="center"
            key="audioInfo"
          >
            <FacialResultIconWrapper>
              <Icon
                iconName="ChatsCircle"
                color={info?.iconColor}
                size="32"
                weight="thin"
              />
            </FacialResultIconWrapper>
            <Label fontSize="12" semibold>
              {t(info?.message)}
            </Label>
          </InfoContainer>
        )}
      </>
    );
  },
);
